/**
 * The browser wiring for packet B815: what feeds the decision in
 * lib/firestoreClosedRecovery.ts, and what a reload and a report are. Read
 * that file first; it holds the problem, the rules and the reasons.
 *
 * WHERE THE SIGNAL COMES FROM, three ways in, all narrow:
 *
 *   1. FIRESTORE'S OWN ERROR LOG, through Firebase's onLog(). This is the one
 *      that sees a listener failure no matter how the page handles it. Read
 *      in the installed SDK: before it throws a b815 the SDK logs the whole
 *      message at error level ("Log the failure in addition to throw an
 *      exception, just in case the exception is swallowed"); the error that
 *      first kills the queue is logged as "INTERNAL UNHANDLED ERROR: ..."; and
 *      a listener that fails on the database is logged ("Initialization of
 *      query ... failed: ...") before its error callback runs. So a page that
 *      catches the throw, or a listener whose error callback just shows an
 *      empty list, is still seen here. Only error level is listened to.
 *   2. The window 'error' event: a b815 thrown somewhere nothing caught it.
 *   3. The window 'unhandledrejection' event: the Sentry issues were all
 *      unhandled, and "The database connection is closing" also arrives
 *      this way from Firebase Auth's own IndexedDB store
 *      (JAVASCRIPT-REACT-T), which closes in the same moment.
 *
 * onLog() only reaches the Firebase loggers that exist when it is called,
 * which is why this file imports lib/firebase.ts: by the time
 * installFirestoreClosedRecovery() can run, Firestore's logger exists.
 *
 * A FOURTH WAY IN, for a Firestore that died WITHOUT a word (2026-11-01,
 * lib/firestoreLiveness.ts says what that is and how it is proven): a local
 * liveness probe, run on a return to the app after a real absence, on a
 * restore from the back/forward cache, and when a tapped save has waited
 * (checkFirestoreAfterSlowSave, then checkFirestoreAfterLongSave a minute in,
 * both called by lib/useSavingText.ts). It reads
 * the SDK's own queue flag, one document from Firestore's on-device cache
 * (getDocFromCache: no network, no rules, nothing billed) and, only when that
 * read does not answer, the browser's IndexedDB directly. A proven death goes
 * to the same controller as 'stalled'; nothing slow ever does.
 *
 * WHEN IT DECIDES: on the first detection, then again only when the person
 * comes back to the app (the page becoming visible, or a restore from the
 * back/forward cache). Never on a timer. The probe's two deadlines are the
 * only timers here, and only a return or a waiting save starts a probe.
 *
 * WHAT IT REPORTS to Sentry: a breadcrumb for each step, and one message when
 * it reloads or when it has to show the bar instead. Which message matched,
 * where it was seen and why the bar showed, and nothing else. From the probe,
 * once per page each: an unproven verdict, and a tapped save still waiting a
 * minute in on a database it could not call dead. WHO it
 * happened to is only ever what setSentryUser (lib/sentry.ts) already put on
 * the scope: uid and role, no name, no email, no dancer.
 *
 * Installed once, from main.tsx, right after Sentry. Every part of it is
 * fenced: if installing throws, the Hub boots exactly as it did before, just
 * without this recovery.
 */
import { onLog } from 'firebase/app';
import { doc, getDocFromCache } from 'firebase/firestore';
import { db } from './firebase';
import { addSentryBreadcrumb, captureSentryMessage } from './sentry';
import { markReload, msSinceLastReload } from './reloadGuard';
import { hasRegisteredUnsavedWork, uploadInFlight } from './unsavedWork';
import { domHasUnsavedText, modalIsOpen } from './unsavedWorkDom';
import {
  createFirestoreRecovery,
  type FirestoreRecoveryController,
  type FirestoreRecoveryEvent,
  type FirestoreRecoverySnapshot,
} from './firestoreClosedRecovery';
import {
  createLivenessSentryGate,
  createStallMonitor,
  type LivenessEnv,
  type LivenessEvent,
  type StallMonitor,
} from './firestoreLiveness';

let controller: FirestoreRecoveryController | null = null;
let stallMonitor: StallMonitor | null = null;

/**
 * The document the probe reads from the cache. It never exists, so the cache
 * answers "not cached" at once on a live Firestore; it is never read from
 * the server, so no rule covers it and nothing is billed.
 */
const LIVENESS_DOC = ['hubLivenessProbe', 'probe'] as const;

/** The SDK internals the probe reads. Pinned by tests/firestore-stalled-recovery. */
type FirestoreInternals = {
  _terminated?: boolean;
  _firestoreClient?: { asyncQueue?: { isShuttingDown?: unknown } };
};

function livenessEnv(): LivenessEnv {
  const internals = () => db as unknown as FirestoreInternals;
  return {
    queueRestricted: () => {
      const flag = internals()._firestoreClient?.asyncQueue?.isShuttingDown;
      return typeof flag === 'boolean' ? flag : null;
    },
    // Nothing to probe before the first Firestore call starts the client (a
    // probe would start it) or after sign-out's own terminate().
    notRunning: () => !db || internals()._terminated === true || !internals()._firestoreClient,
    readCache: () => {
      if (!db) return Promise.resolve(null);
      return getDocFromCache(doc(db, LIVENESS_DOC[0], LIVENESS_DOC[1]));
    },
    readStorage: () => {
      const factory = typeof indexedDB === 'undefined' ? undefined : indexedDB;
      if (!factory) return Promise.resolve('unavailable' as const);
      // Two different asks of the same storage, and EITHER answer counts, so
      // one request a browser happens to be slow or buggy on can never make a
      // live storage look silent. Any outcome at all, an error included, is
      // an answer. A throw while asking proves nothing either way, so it is
      // an answer too: only silence from both is silence.
      return new Promise<'answered'>((resolve) => {
        const answered = () => resolve('answered');
        // databases() only lists names: nothing is opened or written.
        try {
          if (typeof factory.databases === 'function') factory.databases().then(answered, answered);
        } catch {
          answered();
        }
        // A tiny database of the Hub's own, opened and closed again. Created,
        // empty, the first time this ever runs on a device.
        try {
          const request = factory.open('ldg-hub-liveness');
          request.onsuccess = () => {
            try {
              request.result.close();
            } catch {
              // Nothing to close.
            }
            answered();
          };
          request.onerror = answered;
          request.onblocked = answered;
        } catch {
          answered();
        }
      });
    },
    setTimer: (run, ms) => window.setTimeout(run, ms),
    clearTimer: (handle) => window.clearTimeout(handle as number),
    // The wall clock keeps running while an iPhone pauses the page, which is
    // how a late deadline is told from a silent storage.
    now: () => Date.now(),
  };
}

/** The last few probe verdicts, newest last, for a test page or a debugging session. */
const recentLiveness: LivenessEvent[] = [];

/**
 * Each probe verdict as a breadcrumb, and, through the gate, at most once per
 * page each: an unproven stall, and a tapped save still waiting a minute in on
 * a database the probe could not call dead (lib/firestoreLiveness.ts says
 * why each one is sent). Never names a person.
 */
const livenessSentryGate = createLivenessSentryGate();
function reportLivenessToSentry(event: LivenessEvent): void {
  recentLiveness.push(event);
  if (recentLiveness.length > RECENT_EVENT_LIMIT) recentLiveness.shift();
  try {
    const data: Record<string, string> = { channel: event.channel, verdict: event.verdict };
    if (event.proof) data.proof = event.proof;
    addSentryBreadcrumb({
      category: 'firestore-recovery',
      level: 'info',
      message: `Firestore liveness: ${event.verdict}`,
      data,
    });
    for (const sent of livenessSentryGate(event)) {
      captureSentryMessage(sent.message, { level: 'info', tags: sent.tags });
    }
  } catch {
    // Telemetry never decides whether the recovery runs.
  }
}

const NO_BAR: FirestoreRecoverySnapshot = Object.freeze({ barVisible: false });

/** The last few steps, newest last, for a test page or a debugging session. Never names a person. */
const recentEvents: FirestoreRecoveryEvent[] = [];
const RECENT_EVENT_LIMIT = 20;

function reportToSentry(event: FirestoreRecoveryEvent): void {
  recentEvents.push(event);
  if (recentEvents.length > RECENT_EVENT_LIMIT) recentEvents.shift();
  const data: Record<string, string> = {};
  if ('trigger' in event && event.trigger) data.trigger = event.trigger;
  if ('channel' in event) data.channel = event.channel;
  if ('reason' in event) data.reason = event.reason;
  if ('proof' in event && event.proof) data.proof = event.proof;
  try {
    addSentryBreadcrumb({
      category: 'firestore-recovery',
      level: 'info',
      message: `Firestore database closed: ${event.kind}`,
      data,
    });
    if (event.kind === 'reload' || event.kind === 'bar') {
      // No-op when Sentry has no DSN, like reloadGuard.ts's own message.
      captureSentryMessage(`firestore-closed ${event.kind}`, {
        level: 'info',
        tags: {
          firestore_recovery: event.kind,
          firestore_trigger: event.trigger,
          ...('channel' in event ? { firestore_channel: event.channel } : {}),
          ...('reason' in event ? { firestore_recovery_reason: event.reason } : {}),
          ...('proof' in event && event.proof ? { firestore_stall_proof: event.proof } : {}),
        },
      });
    }
  } catch {
    // Telemetry never decides whether the recovery runs.
  }
}

/**
 * window.location.reload(), and whether a page's leave warning held it up.
 *
 * Every Hub page that warns before it is left does it through
 * lib/useLeaveWarning.ts, which also holds this recovery off, so this should
 * only ever see a warning from something that slipped past that rule. When it
 * does, the browser asks "Leave site?", and a person who answers Stay keeps a
 * page whose database is dead. The controller has to know, or it would wait
 * for a reload that is never coming (review of #1393).
 *
 * Chromium runs every beforeunload listener INSIDE reload() itself, before
 * it returns (proven in tests/harness/firestore-closed-recovery, scenario J),
 * so a listener added last, for this one call, sees whether any page
 * objected. True means the person was asked; whether they then left or
 * stayed, the bar is the right thing to have on screen. A browser that asks
 * later, after reload() has returned, reads as false here, and the recheck at
 * the next return to the app catches that page instead.
 */
function reloadUnlessHeld(): boolean {
  let held = false;
  const probe = (event: BeforeUnloadEvent) => {
    if (event.defaultPrevented) held = true;
    else if (typeof event.returnValue === 'string' && event.returnValue.length > 0) held = true;
  };
  window.addEventListener('beforeunload', probe);
  try {
    window.location.reload();
  } finally {
    window.removeEventListener('beforeunload', probe);
  }
  return held;
}

/**
 * Starts listening. Safe to call more than once (the second call does
 * nothing) and safe to call where there is no window or no Firestore.
 */
export function installFirestoreClosedRecovery(): void {
  if (controller) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  // No Firestore (missing env, a failed init): nothing that can close.
  if (!db) return;
  try {
    const created = createFirestoreRecovery({
      now: () => Date.now(),
      isVisible: () => document.visibilityState !== 'hidden',
      uploadInFlight: () => uploadInFlight(),
      hasUnsavedWork: () => hasRegisteredUnsavedWork() || domHasUnsavedText(),
      modalOpen: () => modalIsOpen(),
      msSinceLastReload: () => msSinceLastReload(),
      sessionStore: () => window.sessionStorage,
      markReload: () => markReload(),
      reload: () => reloadUnlessHeld(),
      report: reportToSentry,
    });

    onLog(
      (record) => {
        if (record.level !== 'error') return;
        created.detect(record.message, 'firestore-log');
      },
      { level: 'error' },
    );
    window.addEventListener('error', (event) => {
      if (!created.detect(event.error, 'error')) created.detect(event.message, 'error');
    });
    window.addEventListener('unhandledrejection', (event) => {
      created.detect(event.reason, 'unhandledrejection');
    });
    const stalls = createStallMonitor({
      env: livenessEnv(),
      now: () => Date.now(),
      onDead: (proof, channel) => created.stalled(proof, channel),
      onClosed: (error, channel) => {
        created.detect(error, channel);
      },
      report: reportLivenessToSentry,
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') stalls.hidden();
      else if (document.visibilityState === 'visible') {
        created.recheck();
        stalls.visible();
      }
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        created.recheck();
        stalls.restored();
      }
    });
    controller = created;
    stallMonitor = stalls;
  } catch {
    // Never let the recovery be the thing that breaks a boot.
    controller = null;
    stallMonitor = null;
  }
}

/**
 * A save somebody tapped has waited SLOW_SAVE_MS (lib/useSavingText.ts).
 * Asks the probe once, reading only the phone. Does nothing where the
 * recovery never installed, and never throws.
 */
export function checkFirestoreAfterSlowSave(): void {
  try {
    stallMonitor?.slowSave();
  } catch {
    // A probe that cannot start leaves the button saying Still saving...
  }
}

/**
 * The same save is still waiting at LONG_SAVE_MS (lib/useSavingText.ts). One
 * more probe: dead goes to the recovery as before, anything short of dead is
 * reported to Sentry once per page as 'firestore-save still waiting'. Never
 * throws.
 */
export function checkFirestoreAfterLongSave(): void {
  try {
    stallMonitor?.longSave();
  } catch {
    // The button keeps saying Still saving..., which is still true.
  }
}

export function subscribeFirestoreRecovery(listener: () => void): () => void {
  return controller ? controller.subscribe(listener) : () => {};
}

export function getFirestoreRecoverySnapshot(): FirestoreRecoverySnapshot {
  return controller ? controller.getSnapshot() : NO_BAR;
}

/** The bar's tap. Reloads even when the recovery never installed. */
export function reloadAfterFirestoreClosed(): void {
  if (controller) {
    controller.tapReload();
    return;
  }
  markReload();
  window.location.reload();
}

/** A copy of the last few steps, for a test page or a debugging session. */
export function firestoreRecoveryEvents(): FirestoreRecoveryEvent[] {
  return [...recentEvents];
}

/** A copy of the last few liveness verdicts, for a test page or a debugging session. */
export function firestoreLivenessEvents(): LivenessEvent[] {
  return [...recentLiveness];
}
